import { useSelector, useDispatch } from "react-redux";
import { useSupabase } from "../providers/supabaseProvider";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Grid,
  Divider,
  Tooltip,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Flag as FlagIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TutorialGuide from "../components/TutorialGuide";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import AddEntryDialog from "../entries/addEntryDialog";
import ActivityTable from "../components/ActivityTable";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import FlagDialog from "../components/FlagDialog";
import { getPlayerEntries, deleteEntry } from "../features/entry/entrySlice";
import { Fragment } from "react";
import {
  getUserProfile,
  updateUserProfile,
} from "../features/userProfile/userProfileSlice";
import { getPlayerByBSANumber } from "../features/player/playerSlice";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
};

function MyRecord() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useKindeAuth();
  const sessionToken = useSelector((state) => state.auth.sessionToken);

  // State for lookup caches
  const [categoriesMap, setCategoriesMap] = useState(new Map());
  const [positionsMap, setPositionsMap] = useState(new Map());
  const [categoryTypesMap, setCategoryTypesMap] = useState(new Map());
  const [lookupsLoading, setLookupsLoading] = useState(true);

  // Use the Supabase provider instance
  const supabase = useSupabase();

  // Helper function to get category name from ID
  const getCategoryName = (categoryId) => {
    if (!categoryId) return "N/A";
    return categoriesMap.get(parseInt(categoryId)) || "N/A";
  };

  // Helper function to get position name from ID
  const getPositionName = (positionId) => {
    if (!positionId) return "N/A";
    return positionsMap.get(parseInt(positionId)) || "N/A";
  };

  // Helper function to get category type name from ID
  const getCategoryTypeName = (categoryTypeId) => {
    if (!categoryTypeId) return "N/A";
    return categoryTypesMap.get(parseInt(categoryTypeId)) || "N/A";
  };

  // Fetch categories and positions on mount
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [categoriesRes, positionsRes, categoryTypesRes] =
          await Promise.all([
            supabase.from("categories").select("id, name"),
            supabase.from("positions").select("id, name"),
            supabase.from("category_type").select("id, name"),
          ]);

        if (categoriesRes.error) throw categoriesRes.error;
        if (positionsRes.error) throw positionsRes.error;
        // category_type is optional, so don't throw if it fails

        // Create lookup maps
        const catMap = new Map(
          categoriesRes.data.map((cat) => [cat.id, cat.name]),
        );
        const posMap = new Map(
          positionsRes.data.map((pos) => [pos.id, pos.name]),
        );
        const catTypeMap = new Map(
          categoryTypesRes.data?.map((ct) => [ct.id, ct.name]) || [],
        );

        setCategoriesMap(catMap);
        setPositionsMap(posMap);
        setCategoryTypesMap(catTypeMap);
      } catch (error) {
        console.error("Error fetching lookups:", error);
      } finally {
        setLookupsLoading(false);
      }
    };

    fetchLookups();
  }, []);

  // Extract user name from profile or auth metadata
  const getUserName = (user) => {
    if (!user) return null;

    return (
      user.user_metadata?.full_name ||
      user.user_metadata?.name ||
      (user.user_metadata?.first_name && user.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : null) ||
      user.email ||
      "Unknown User"
    );
  };

  // User profile and player state
  const userProfile = useSelector((state) => state.userProfile.profile);
  const userProfileLoading = useSelector(
    (state) => state.userProfile.isLoading,
  );

  // Entries state
  const entries = useSelector((state) => state.entry.entries);
  const entriesLoading = useSelector((state) => state.entry.isLoading);
  const isDeletingEntry = useSelector((state) => state.entry.isDeleting);
  const userProfileUpdating = useSelector(
    (state) => state.userProfile.isLoading,
  );

  // Player state from Redux
  const playerRecord = useSelector((state) => state.player.player);
  const playerLoading = useSelector((state) => state.player.isLoading);

  const [entryDialog, setEntryDialog] = useState({
    open: false,
    editEntry: null, // null for add mode, entry object for edit mode
  });

  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    item: null,
  });

  // BSA Number dialog state
  const [bsaDialogOpen, setBsaDialogOpen] = useState(false);
  const [bsaInput, setBsaInput] = useState("");

  // Flag Dialog state
  const [flagDialog, setFlagDialog] = useState({
    open: false,
    event: null,
    playerName: null,
  });

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserProfile()).catch((error) => {
        console.error("Error dispatching getUserProfile:", error);
      });
    }
  }, [isAuthenticated, navigate, dispatch]);

  // Effect: When user profile is loaded, get or create player
  useEffect(() => {
    // Only fetch player if we have a BSA number
    if (!userProfile?.bsa_number) {
      return;
    }

    dispatch(getPlayerByBSANumber(userProfile.bsa_number));
  }, [userProfile?.bsa_number, dispatch]);

  // Effect: When player is loaded, fetch entries
  useEffect(() => {
    if (!playerRecord?.id) {
      return;
    }

    dispatch(getPlayerEntries(playerRecord.id)).catch((error) => {
      console.error("Error fetching entries:", error);
    });
  }, [playerRecord?.id, dispatch]);

  // Effect: Auto-open BSA dialog if no BSA number
  useEffect(() => {
    if (userProfile && !userProfile.bsa_number) {
      setBsaDialogOpen(true);
    }
  }, [userProfile?.bsa_number]);

  const handleAddEntry = () => {
    setEntryDialog({ open: true, editEntry: null });
  };

  const handleEditEntry = (entry) => {
    setEntryDialog({ open: true, editEntry: entry });
  };

  const handleDeleteEntry = (entry) => {
    setDeleteDialog({
      open: true,
      type: "entry",
      item: entry,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.item) return;

    dispatch(deleteEntry(deleteDialog.item.id))
      .unwrap()
      .then(() => {
        setDeleteDialog({ open: false, type: null, item: null });
      });
  };

  const handleOpenBSADialog = () => {
    setBsaInput("");
    setBsaDialogOpen(true);
  };

  const handleCloseBSADialog = () => {
    setBsaDialogOpen(false);
    setBsaInput("");
  };

  const handleSaveBSA = () => {
    if (bsaInput.trim()) {
      dispatch(updateUserProfile({ bsa_number: bsaInput.trim() })).then(() => {
        handleCloseBSADialog();
      });
    }
  };

  const handleOpenFlagDialog = (entry) => {
    setFlagDialog({
      open: true,
      event: {
        name: entry.event,
        date: entry.event_date,
        formattedDate: formatDate(entry.event_date),
        position: entry.position?.name || entry.position || "N/A",
      },
      playerName: userProfile?.first_name + " " + userProfile?.last_name,
    });
  };

  const handleCloseFlagDialog = () => {
    setFlagDialog({ open: false, event: null, playerName: null });
  };

  if (userProfileLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{ mt: 4, py: 4 }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading your profile...
        </Typography>
      </Box>
    );
  }

  if (userProfile?.bsa_number && (playerLoading || !playerRecord)) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{ mt: 4, py: 4 }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading your ranking...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 } }}>
      <TutorialGuide />
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        sx={{ mb: 3, flexWrap: "wrap" }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
        >
          My Ranking
        </Typography>
      </Box>

      {/* User Information Card - Responsive Layout */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={{ xs: 2, sm: 4 }}
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            {/* Left - Ranking Focus */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "action.hover",
                borderRadius: 2,
                padding: { xs: 2, sm: 3 },
                minWidth: { xs: "100%", sm: 200 },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Current Ranking
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "bold",
                  color: "primary.main",
                  lineHeight: 1,
                }}
              >
                {playerRecord?.rank || "--"}
              </Typography>
              <Divider sx={{ width: "100%", my: 2 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Qualifying Points
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "600",
                  color: "primary.main",
                }}
              >
                {playerRecord?.total_points
                  ? playerRecord.total_points.toFixed(2)
                  : "--"}
              </Typography>
            </Box>

            {/* Right - User Info */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: "600", mb: 3 }}
              >
                {userProfile
                  ? `${userProfile.first_name} ${userProfile.last_name}`
                  : getUserName(user)}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      backgroundColor: "action.hover",
                      borderRadius: 1,
                      padding: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      BSA Number
                    </Typography>
                    {!userProfile?.bsa_number ? (
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography color="text.secondary" variant="body2">
                          Not Set
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleOpenBSADialog}
                        >
                          Add BSA Number
                        </Button>
                      </Box>
                    ) : (
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{ fontWeight: "600" }}
                      >
                        {userProfile?.bsa_number}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {userProfile?.bsa_number && !entriesLoading ? (
                  <Fragment>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          padding: 2,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 1 }}
                        >
                          Total Entries
                        </Typography>
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ fontWeight: "600" }}
                        >
                          {entries.length}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          padding: 2,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 1 }}
                        >
                          Total Points
                        </Typography>
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{ fontWeight: "600" }}
                        >
                          {entries
                            .reduce(
                              (sum, entry) => sum + (entry.points || 0),
                              0,
                            )
                            .toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Fragment>
                ) : null}

                {/* <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      backgroundColor: "action.hover",
                      borderRadius: 1,
                      padding: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Status
                    </Typography>
                    <Chip
                      label={entries.length >= 1 ? "Active" : "Inactive"}
                      color={entries.length >= 3 ? "success" : "default"}
                      variant="outlined"
                    />
                  </Box>
                </Grid> */}
              </Grid>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Main Layout - 70/30 Split - Only show if BSA number exists */}
      {userProfile?.bsa_number ? (
        <Fragment>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              width: "100%",
            }}
          >
            {/* Left Column - Entries Section (70%) */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 70%" } }}>
              <Paper elevation={3}>
                <Box p={2}>
                  <Box display={"flex"} flexDirection={"column"}>
                    <Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="h6">
                          My Competition Entries
                        </Typography>
                        <Button
                          data-tutorial="add-entry-button"
                          startIcon={<AddIcon />}
                          variant="contained"
                          onClick={handleAddEntry}
                        >
                          Add Entry
                        </Button>
                      </Box>

                      {/* Add Entry Dialog */}
                      <AddEntryDialog
                        open={entryDialog.open}
                        onClose={() =>
                          setEntryDialog({ open: false, editEntry: null })
                        }
                        playerId={playerRecord?.id}
                        editEntry={entryDialog.editEntry}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="warning.main"
                      sx={{
                        border: "1.5px solid",
                        borderColor: "warning.main",
                        p: 2,
                        borderRadius: 1,
                        background: "transparent",
                      }}
                    >
                      <strong>Notice:</strong> The first incorrect entry made by
                      an individual will be considered an innocent mistake. A
                      second incorrect entry will result in a final warning.
                      Upon a third incorrect entry, the individual will be
                      removed from the ranking.
                    </Typography>
                  </Box>
                  {/* Entries Table - Mobile Responsive */}
                  {!entriesLoading ? (
                    <Box sx={{ overflowX: "auto", width: "100%" }}>
                      <Table
                        size="small"
                        sx={{ minWidth: { xs: 500, sm: "auto" } }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Event</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Category Type</TableCell>
                            <TableCell>Position</TableCell>
                            <TableCell>District</TableCell>
                            <TableCell>Points</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {lookupsLoading ? (
                            <TableRow>
                              <TableCell colSpan={9} align="center">
                                <CircularProgress size={24} />
                              </TableCell>
                            </TableRow>
                          ) : entries.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} align="center">
                                <Typography color="text.secondary">
                                  No entries found. Add your first competition
                                  entry!
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            entries.map((entry) => {
                              const isImported = entry.bowlscout_import
                                ? true
                                : false;
                              const categoryName =
                                entry.category?.name ||
                                getCategoryName(entry.category);
                              const positionName =
                                entry.position?.name ||
                                getPositionName(entry.position);
                              return (
                                <TableRow key={entry.id}>
                                  <TableCell>{entry.event}</TableCell>
                                  <TableCell>
                                    {formatDate(entry.event_date)}
                                  </TableCell>
                                  <TableCell>{categoryName}</TableCell>
                                  <TableCell>
                                    {getCategoryTypeName(entry.category_type)}
                                  </TableCell>
                                  <TableCell>{positionName}</TableCell>
                                  <TableCell>
                                    {entry.district || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {entry.points
                                      ? entry.points.toFixed(2)
                                      : "0.00"}
                                  </TableCell>
                                  <TableCell>
                                    {entry.is_valid === false ? (
                                      <Tooltip
                                        title={`Marked as invalid: ${entry.invalid_reason}`}
                                      >
                                        <Chip
                                          label="Invalid"
                                          color="error"
                                          size="small"
                                          variant="outlined"
                                        />
                                      </Tooltip>
                                    ) : entry.is_valid === null ? (
                                      <Tooltip title="This entry is pending review.">
                                        <Chip
                                          label="Pending"
                                          color="warning"
                                          size="small"
                                          variant="outlined"
                                        />
                                      </Tooltip>
                                    ) : (
                                      <Chip
                                        label="Valid"
                                        color="success"
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {isImported ? (
                                      <Box display="flex" gap={1}>
                                        <Tooltip title="Flag this to update if this is not correct">
                                          <Chip label="Imported" size="small" />
                                        </Tooltip>
                                        <Chip
                                          icon={<FlagIcon />}
                                          label="Flag"
                                          variant="outlined"
                                          color="error"
                                          size="small"
                                          onClick={() =>
                                            handleOpenFlagDialog(entry)
                                          }
                                        />
                                      </Box>
                                    ) : (
                                      <Box display="flex" gap={1}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleEditEntry(entry)}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            handleDeleteEntry(entry)
                                          }
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>{" "}
                    </Box>
                  ) : (
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      sx={{ py: 4 }}
                    >
                      <CircularProgress />
                      <Typography variant="body1" sx={{ ml: 2, mt: 2 }}>
                        Loading your entries...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>

            {/* Right Column - Activity Sidebar (30%) */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 30%" }, minWidth: 0 }}>
              {playerRecord?.id && (
                <Paper elevation={3} sx={{ p: 2, height: "fit-content" }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: "1.1rem" }}
                  >
                    Recent Activity
                  </Typography>
                  <ActivityTable
                    playerId={playerRecord?.id}
                    compact={true}
                    maxItems={10}
                  />
                </Paper>
              )}
            </Box>
          </Box>
          <Box>
            {playerRecord?.qualifying_events &&
              playerRecord?.qualifying_events.length > 0 && (
                <Card
                  sx={{
                    mt: 6,
                    mb: 3,
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: "700",
                          fontSize: "1.1rem",
                          fontFamily: "'Roboto', sans-serif",
                        }}
                      >
                        📈 Points Accumulation
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "'Roboto', sans-serif" }}
                      >
                        Total Qualifying Points:{" "}
                        {playerRecord.total_points?.toFixed(2) || "--"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 2,
                        padding: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart
                          data={[...(playerRecord?.qualifying_events || [])]
                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                            .map((event, index, sortedEvents) => {
                              const cumulativePoints = sortedEvents
                                .slice(0, index + 1)
                                .reduce((sum, e) => sum + e.points, 0);
                              return {
                                date: new Date(event.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                  },
                                ),
                                points: cumulativePoints,
                                eventName: event.name,
                              };
                            })}
                          margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                        >
                          <defs>
                            <linearGradient
                              id="colorPoints"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#1976d2"
                                stopOpacity={0.3}
                              />
                              <stop
                                offset="95%"
                                stopColor="#1976d2"
                                stopOpacity={0}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e0e0e0"
                          />
                          <XAxis
                            dataKey="date"
                            stroke="#666"
                            style={{
                              fontSize: "12px",
                              fontFamily: "'Roboto', sans-serif",
                            }}
                          />
                          <YAxis
                            stroke="#666"
                            style={{
                              fontSize: "12px",
                              fontFamily: "'Roboto', sans-serif",
                            }}
                          />
                          <RechartsTooltip
                            formatter={(value) => [
                              `${value.toFixed(2)} pts`,
                              "Total Points",
                            ]}
                            labelFormatter={(label) => `${label}`}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.98)",
                              border: "2px solid #1976d2",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              padding: "10px",
                              fontFamily: "'Roboto', sans-serif",
                            }}
                            cursor={{
                              strokeDasharray: "3 3",
                              stroke: "#1976d2",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="points"
                            stroke="#1976d2"
                            strokeWidth={3}
                            dot={{
                              fill: "#1976d2",
                              r: 5,
                              strokeWidth: 2,
                              stroke: "white",
                            }}
                            activeDot={{ r: 7, strokeWidth: 2 }}
                            fillOpacity={1}
                            fill="url(#colorPoints)"
                            isAnimationActive={true}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              )}
          </Box>
        </Fragment>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 4, py: 4 }}
        >
          <Typography variant="body1" color="text.secondary">
            Please add your BSA number to view your competition entries and
            activity.
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, item: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this entry? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteDialog({ open: false, type: null, item: null })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeletingEntry}
          >
            {isDeletingEntry ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flag Dialog */}
      <FlagDialog
        open={flagDialog.open}
        onClose={handleCloseFlagDialog}
        event={flagDialog.event}
        playerName={flagDialog.playerName}
      />

      {/* BSA Number Dialog */}
      <Dialog open={bsaDialogOpen} onClose={handleCloseBSADialog}>
        <DialogTitle>Add BSA Number</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="BSA Number"
              placeholder="Enter your BSA number"
              value={bsaInput}
              onChange={(e) => setBsaInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && bsaInput.trim()) {
                  handleSaveBSA();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBSADialog}>Cancel</Button>
          <Button
            onClick={handleSaveBSA}
            variant="contained"
            disabled={!bsaInput.trim() || userProfileUpdating}
          >
            {userProfileUpdating ? "Saving..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MyRecord;
